import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Truck, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useDeliverySettings } from '@/features/settings/hooks/useSettings';
import { useTrayNotices } from '../NoticeTray';
import {
  DELIVERY_SETTINGS_PATH,
  readDeliverySetupDismissal,
  writeDeliverySetupDismissal,
} from './delivery-setup-notice';
import styles from './DeliverySetupBanner.module.css';

/**
 * Avisa o dono do restaurante que a loja está publicada sem configuração de
 * entrega — e que, por isso, o cardápio está aceitando só retirada.
 *
 * Antes deste banner, o dono não tinha como saber: em 17/08/2026 o cardápio
 * público chegou a perder todos os pedidos de entrega de estabelecimentos
 * recém-cadastrados, em silêncio, e o único sinal era um cliente reclamando.
 * Hoje o carrinho força retirada em vez de perder o pedido, mas do lado de
 * quem pode resolver continuava não havendo aviso nenhum.
 *
 * Global e não só no dashboard, pela mesma razão do TrialBanner: o dono opera
 * o dia inteiro na tela de pedidos e pode passar semanas sem abrir o dashboard,
 * enquanto a loja publicada recusa entrega o tempo todo.
 */
export function DeliverySetupBanner() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const canConfigure = useAuthStore(
    (s) => s.user?.permissions.includes('settings.write') ?? false,
  );
  const { pathname } = useLocation();

  // Guarda as dispensas da sessão à parte do storage: se a escrita falhar, o
  // clique ainda esconde o banner. Fica no componente externo, que não
  // desmonta ao navegar, para a dispensa sobreviver à troca de tela.
  const [dismissedThisSession, setDismissedThisSession] = useState<readonly string[]>([]);

  if (!canConfigure) return null;
  if (pathname === DELIVERY_SETTINGS_PATH) return null;

  // A leitura do storage acontece em render de propósito: a chave é por tenant,
  // então trocar de estabelecimento já reavalia a dispensa sem efeito nenhum.
  if (dismissedThisSession.includes(tenantId) || readDeliverySetupDismissal(tenantId)) {
    return null;
  }

  // A consulta mora no filho para não sair requisição de quem não pode agir
  // sobre a resposta — atendente sem `settings.write`, tela de entrega, aviso
  // já dispensado. Nesses casos nada é buscado.
  return (
    <DeliverySetupNotice
      onDismiss={() => {
        writeDeliverySetupDismissal(tenantId);
        setDismissedThisSession((prev) => [...prev, tenantId]);
      }}
    />
  );
}

function DeliverySetupNotice({ onDismiss }: { onDismiss: () => void }) {
  const { data } = useDeliverySettings();

  // `=== false` de propósito: `undefined` é "carregando" ou "a consulta
  // falhou", e nenhum dos dois é "não configurado". Erro de rede virando aviso
  // é o falso positivo que este banner não pode dar.
  const notConfigured = data?.configured === false;
  useTrayNotices(
    notConfigured ? [{ key: 'delivery-setup', text: 'O cardápio está aceitando apenas retirada', level: 'normal' }] : [],
  );
  if (!notConfigured) return null;

  return (
    <div className={styles.banner} role="status">
      {/*
        Mesma estrutura do TrialBanner: ícone, texto e link num bloco só, para
        o × não cair sozinho numa terceira linha no celular.
      */}
      <div className={styles.message}>
        <Truck size={16} className={styles.icon} />
        <span className={styles.text}>
          Seu cardápio está aceitando <strong>apenas retirada</strong>. Configure a entrega
          para receber pedidos com endereço.
        </span>
        <Link to={DELIVERY_SETTINGS_PATH} className={styles.link}>
          Configurar entrega
        </Link>
      </div>
      <button
        className={styles.dismissBtn}
        onClick={onDismiss}
        aria-label="Fechar aviso de entrega não configurada"
      >
        <X size={14} strokeWidth={1.5} />
      </button>
    </div>
  );
}
